import type { CallEvaluation, CallDetail } from '../api';
import type { T } from '../i18n';

export const SCORE_KEYS = ['opening', 'discovery', 'objection_handling', 'closing', 'clarity'] as const;
/** Flags where "yes" is good (rules said); the others are mistakes or alerts. */
export const GOOD_FLAGS = ['said_recording_notice', 'said_ai_disclosure'] as const;
export const BAD_FLAGS = ['agent_missed_question', 'agent_repeated', 'agent_ended_early', 'alert_complaint'] as const;

export const isEvaluation = (e: CallDetail['evaluation']): e is CallEvaluation => !!e && (e as CallEvaluation).version === 1;
const tt = (t: T, key: string, fallback: string) => (t(key) === key ? fallback : t(key));

/** ADR-047: one call's scorecard from the decision model (JEV): scores, rules, mistakes, outcome, questions. */
export const EvaluationCard = ({ t, e }: { t: T; e: CallEvaluation }) => {
  const unsure = new Set(e.unsure);
  const check = (k: string) => (unsure.has(k) ? <span className="tag warn" title={t('eval.unsureHint')}>{t('eval.unsure')}</span> : null);
  const choice = (k: string) => (e.choices[k] ? tt(t, `eval.choice.${k}.${e.choices[k]}`, e.choices[k]) : '—');
  return (
    <div className="card evaluation">
      <div className="row-between">
        <h3>{t('eval.title')}</h3>
        <span className="muted small mono">{e.model}</span>
      </div>
      <div className="hbars">
        {SCORE_KEYS.filter((k) => e.scores[k] !== undefined).map((k) => (
          <div key={k} className="hbar">
            <span>{t(`eval.score.${k}`)} {check(k)}</span>
            <span className="hbar-track"><span className="hbar-fill" style={{ width: `${((e.scores[k]! - 1) / 4) * 100}%` }} /></span>
            <span className="hbar-value">{e.scores[k]!.toFixed(1)} / 5</span>
          </div>
        ))}
      </div>
      <div className="eval-grid">
        <div><span className="muted small">{t('eval.choice.outcome')}</span><strong>{choice('outcome')}</strong> {check('outcome')}</div>
        {e.choices.loss_reason && e.choices.loss_reason !== 'not_applicable' && <div><span className="muted small">{t('eval.choice.loss_reason')}</span><strong>{choice('loss_reason')}</strong> {check('loss_reason')}</div>}
        {e.choices.drop_stage && e.choices.drop_stage !== 'none' && <div><span className="muted small">{t('eval.choice.drop_stage')}</span><strong>{choice('drop_stage')}</strong> {check('drop_stage')}</div>}
        <div><span className="muted small">{t('eval.choice.sentiment_end')}</span><strong>{choice('sentiment_end')}</strong></div>
      </div>
      <div className="eval-flags">
        {GOOD_FLAGS.filter((k) => e.flags[k] !== undefined).map((k) => (
          <span key={k} className={`tag ${e.flags[k]! >= 0.5 ? 'ok' : 'warn'}`}>{e.flags[k]! >= 0.5 ? '✓' : '✗'} {t(`eval.flag.${k}`)}</span>
        ))}
        {BAD_FLAGS.filter((k) => (e.flags[k] ?? 0) >= 0.5).map((k) => (
          <span key={k} className="tag warn">⚠ {t(`eval.flag.${k}`)}</span>
        ))}
      </div>
      {e.questions.length > 0 && (
        <>
          <h4>{t('eval.questions', { n: e.questions.length, a: e.questions.filter((q) => q.answered >= 0.5).length })}</h4>
          <ul className="eval-questions">
            {e.questions.map((q, i) => (
              <li key={i}>
                <span className={`tag ${q.answered >= 0.5 ? 'ok' : 'warn'}`}>{q.answered >= 0.5 ? t('eval.answered') : t('eval.notAnswered')}</span>
                {q.intent === 'objection' && <span className="tag">{t('eval.objection')}</span>}
                <span>{q.text}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
